import React from "react";
import { Users, Building2, UserCheck, Mail, BookOpen } from "lucide-react";
import AdminLayout from "../../components/layouts/AdminLayout";

const cards = [
  {
    title: "Companies",
    description: "Manage company profiles, approvals, and global settings.",
    link: "/admin/companies",
    icon: Building2,
    accent: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    title: "Farmers",
    description: "Onboard, review compliance, and support farmer operations.",
    link: "/admin/farmers",
    icon: UserCheck,
    accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "Customers",
    description: "Oversee customer accounts, KYC, and engagement.",
    link: "/admin/customers",
    icon: Users,
    accent: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    title: "Landing Messages",
    description: "Review and respond to contact messages from the landing page.",
    link: "/admin/messages",
    icon: Mail,
    accent: "bg-purple-50 text-purple-700 border-purple-200",
  },
  {
    title: "Accounting",
    description: "Open bookkeeping records, ledgers, and accounting analytics tabs.",
    link: "/admin/accounting",
    icon: BookOpen,
    accent: "bg-sky-50 text-sky-700 border-sky-200",
  },
];

const QuickStat = ({ label, value, trend }) => (
  <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <div className="mt-2 flex items-baseline gap-2">
      <p className="text-2xl font-semibold text-slate-900">{value}</p>
      {trend && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{trend}</span>}
    </div>
  </div>
);

const AdminDashboard = () => {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <section className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <QuickStat label="Active Companies" value="24" trend="+3 this week" />
          <QuickStat label="Total Farmers" value="1,240" trend="+56" />
          <QuickStat label="Customers" value="5,430" trend="+210" />
          <QuickStat label="Open Messages" value="18" trend="2 new" />
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map(({ title, description, link, icon: Icon, accent }) => (
            <a
              key={title}
              href={link}
              className={`block border rounded-xl p-4 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition ${accent}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="h-10 w-10 rounded-lg bg-white/70 text-slate-900 flex items-center justify-center border border-white/60 shadow-inner">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="text-xs text-slate-600">Control center</p>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{description}</p>
              <div className="mt-3 text-xs font-semibold text-slate-800 underline">Manage</div>
            </a>
          ))}
        </section>

        <section className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">What you can do</h2>
          <p className="text-sm text-slate-600 mb-4">
            Act as the orchestrator across companies, farmers, and customers. Approve, suspend, or update any entity, and respond to
            new inquiries coming from the public landing page.
          </p>
          <ul className="space-y-2 text-sm text-slate-700 list-disc list-inside">
            <li>Approve or suspend companies and manage their employees</li>
            <li>Onboard farmers, link them to companies, and monitor compliance</li>
            <li>Manage customer accounts, payments, and escalations</li>
            <li>Review and respond to landing page contact messages</li>
          </ul>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
